import React, { useState, useEffect } from 'react'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { User } from '@shared/types'
import { UpdateUserData, updateUserSchema } from '@shared'
import { UserService } from '@/services/userService'
import { getErrorMessage, isValidationError } from '@/utils/errorHandling'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import FormField from '@/components/ui/FormField'
import { useToast } from '@/hooks/useToast'

interface EditUserModalProps {
  user: User | null
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onError: (error: string) => void
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
  onError
}) => {
  const [updateLoading, setUpdateLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { toast } = useToast()

  // React Hook Form setup for editing users
  const methods = useForm<UpdateUserData>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'HR_ADMIN',
      branchId: ''
    }
  })

  const { handleSubmit, reset, setError: setFormError, setValue, control } = methods

  // Watch role to show/hide branchId field
  const selectedRole = useWatch({ control, name: 'role' })
  const showBranchId = selectedRole === 'BRANCH_MANAGER'

  // Clear branchId when switching away from BRANCH_MANAGER
  useEffect(() => {
    if (selectedRole !== 'BRANCH_MANAGER') {
      setValue('branchId', null)
    }
  }, [selectedRole, setValue])

  // Populate form when user changes
  useEffect(() => {
    if (user) {
      setValue('email', user.email)
      setValue('firstName', user.firstName)
      setValue('lastName', user.lastName)
      setValue('branchId', user.branchId || '')

      // Map frontend role to backend role format for the form
      const backendRoleMap: Record<User['role'], 'HR_ADMIN' | 'GENERAL_MANAGER' | 'BRANCH_MANAGER' | 'SYSTEM_ADMIN' | 'UNASSIGNED'> = {
        'hr_admin': 'HR_ADMIN',
        'general_manager': 'GENERAL_MANAGER',
        'branch_manager': 'BRANCH_MANAGER',
        'system_admin': 'SYSTEM_ADMIN',
        'unassigned': 'UNASSIGNED'
      }
      setValue('role', backendRoleMap[user.role] as 'HR_ADMIN' | 'GENERAL_MANAGER' | 'BRANCH_MANAGER' | 'SYSTEM_ADMIN')
      setValue('password', '') // Always start with empty password
    }
  }, [user, setValue])

  // Handle form submission
  const onSubmit = async (data: UpdateUserData) => {
    if (!user) return

    try {
      setUpdateLoading(true)

      // Remove empty fields from the update data
      const updateData: UpdateUserData = {}
      if (data.email && data.email !== user.email) updateData.email = data.email
      if (data.firstName && data.firstName !== user.firstName) updateData.firstName = data.firstName
      if (data.lastName && data.lastName !== user.lastName) updateData.lastName = data.lastName
      if (data.role && data.role !== user.role.toUpperCase()) updateData.role = data.role
      if (data.password && data.password.trim()) updateData.password = data.password
      // Handle branchId - include if changed (can be null to clear it)
      if (data.branchId !== (user.branchId || '')) {
        updateData.branchId = data.branchId || null
      }

      // Only update if there are changes
      if (Object.keys(updateData).length === 0) {
        onError('No changes detected')
        return
      }

      await UserService.updateUser(user.id, updateData)

      // Close modal and notify success
      handleClose()
      onSuccess()
      toast.success('User updated successfully', `${user.firstName} ${user.lastName} has been updated successfully`)
    } catch (err) {
      // Handle form errors with type-safe approach
      if (isValidationError(err, 'email already exists')) {
        setFormError('email', {
          type: 'manual',
          message: 'A user with this email already exists'
        })
      } else {
        const errorMessage = getErrorMessage(err, 'Failed to update user')
        onError(errorMessage)
        toast.error('Update failed', errorMessage)
      }
    } finally {
      setUpdateLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    reset()
    setShowPassword(false)
    onClose()
  }

  if (!isOpen || !user) return null

  const roleOptions = [
    { value: 'HR_ADMIN', label: 'HR Admin' },
    { value: 'GENERAL_MANAGER', label: 'General Manager' },
    { value: 'BRANCH_MANAGER', label: 'Branch Manager' },
    { value: 'SYSTEM_ADMIN', label: 'System Admin' },
    { value: 'UNASSIGNED', label: 'Unassigned' }
  ]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg border border-border p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Edit User</h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
            disabled={updateLoading}
          >
            ×
          </button>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
              required
              disabled={updateLoading}
            />

            <FormField
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
              required
              disabled={updateLoading}
            />

            <FormField
              name="email"
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              required
              disabled={updateLoading}
            />

            <FormField
              name="password"
              label="New Password"
              type="password"
              inputType={showPassword ? 'text' : 'password'}
              placeholder="Enter new password (min 8 characters)"
              disabled={updateLoading}
              rightIcon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              onRightIconClick={() => setShowPassword(!showPassword)}
              helperText="(leave blank to keep current)"
            />

            <FormField
              name="role"
              label="Role"
              type="select"
              options={roleOptions}
              disabled={updateLoading}
            />

            {showBranchId && (
              <FormField
                name="branchId"
                label="Store ID"
                placeholder="e.g., 001, 002, 003"
                required
                disabled={updateLoading}
                helperText="The store ID this manager is responsible for"
              />
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={updateLoading}
                className="px-4 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateLoading}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updateLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {updateLoading ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}

export default EditUserModal 